/**
 * Server-Funktionen für die Decision-Run-Detailansicht.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const RunInput = z.object({ run_id: z.string().uuid() });

export type RunDetail = {
  id: string;
  engine_version: string;
  ruleset_version: string | null;
  prompt_version: string | null;
  phase_type: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  trigger_source: string | null;
  economically_best_code: string | null;
  energy_prime_code: string | null;
  tie_breaker_applied: boolean | null;
  tie_breaker_reason: string | null;
  output_summary: Record<string, any> | null;
  last_error: string | null;
  created_at: string;
  project_no: string | null;
  project_name: string | null;
  project_id: string;
  variant_count: number;
};

export type VariantSummary = {
  variant_code: string;
  label: string;
  investment_brutto_eur: number | null;
  zuschuss_eur: number | null;
  tilgungszuschuss_eur: number | null;
  nettoinvestition_eur: number | null;
  foerderdarlehen_eur: number | null;
  bankdarlehen_eur: number | null;
  eigenkapital_eur: number | null;
  npv_20y_eur: number | null;
  payback_years: number | null;
  irr_pct: number | null;
  autarky_pct: number | null;
  is_economically_best: boolean | null;
  is_energy_prime: boolean | null;
  trust_level: string;
  risk_level: string;
  // Stufe 4D
  co2_baseline_t_year: number | null;
  co2_after_t_year: number | null;
  co2_savings_t_year: number | null;
  co2_savings_20y_t: number | null;
  mieterstrom_netto_eur: number | null;
  mieterwaerme_netto_eur: number | null;
  subsidy_eur: number | null;
  // Masterblock 2 — Quality-Block
  compatibility_score: number | null;
  compatibility_status: string | null;
  manufacturer_score: number | null;
  warranty_score: number | null;
  service_score: number | null;
  future_proof_score: number | null;
  risk_score: number | null;
  quality_score: number | null;
  risk_scenarios: Record<string, any> | null;
  offer_comparison: Record<string, any> | null;
  // Masterblock 4 — Gebäude/Sanierung
  building_score: number | null;
  envelope_score: number | null;
  renovation_need_score: number | null;
  heatpump_suitability_score: number | null;
  renovation_priority_score: number | null;
  building_risk_score: number | null;
  building_assessment: Record<string, any> | null;
  renovation_roadmap: Record<string, any> | null;
  // Masterblock 5 — Wirtschaftlichkeit
  tco_10y_eur: number | null;
  tco_20y_eur: number | null;
  tco_30y_eur: number | null;
  cashflow_10y_eur: number | null;
  cashflow_20y_eur: number | null;
  cashflow_30y_eur: number | null;
  monthly_net_effect_eur: number | null;
  roi_equity_pct: number | null;
  roi_total_pct: number | null;
  seven_fold_returns: Record<string, any> | null;
  economics_scenarios: Record<string, any> | null;
  economics_block: Record<string, any> | null;
  // Masterblock 6 — Förder-/Steuer-/Regulatorik
  subsidy_block: Record<string, any> | null;
  tax_potential_block: Record<string, any> | null;
  regulatory_risk_score: number | null;
  compliance_status: string | null;
  compliance_block: Record<string, any> | null;
  compliance_sources: Record<string, any> | null;
  customer_disclosure: Record<string, any> | null;
  compliance_engine_version: string | null;
  reasoning: Record<string, any> | null;
};

export const getDecisionRunDetail = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => RunInput.parse(i))
  .handler(async ({ data, context }) => {
    const { supabase } = context;

    // Run + Projekt-Infos in einem Query
    const { data: runRow, error: runErr } = await supabase
      .from("decision_runs")
      .select(
        "id, engine_version, ruleset_version, prompt_version, phase_type, status, started_at, finished_at, trigger_source, economically_best_code, energy_prime_code, tie_breaker_applied, tie_breaker_reason, output_summary, last_error, created_at, project_id, projects(project_no, name)"
      )
      .eq("id", data.run_id)
      .single();

    if (runErr) throw new Error(`Run nicht gefunden: ${runErr.message}`);
    if (!runRow) throw new Error("Run nicht gefunden");

    // Varianten laden
    const { data: variants, error: varErr } = await supabase
      .from("decision_variants")
      .select(
        "variant_code, label, investment_brutto_eur, zuschuss_eur, tilgungszuschuss_eur, nettoinvestition_eur, foerderdarlehen_eur, bankdarlehen_eur, eigenkapital_eur, npv_20y_eur, payback_years, irr_pct, autarky_pct, is_economically_best, is_energy_prime, trust_level, risk_level, co2_baseline_t_year, co2_after_t_year, co2_savings_t_year, co2_savings_20y_t, mieterstrom_netto_eur, mieterwaerme_netto_eur, subsidy_eur, compatibility_score, compatibility_status, manufacturer_score, warranty_score, service_score, future_proof_score, risk_score, quality_score, risk_scenarios, offer_comparison, building_score, envelope_score, renovation_need_score, heatpump_suitability_score, renovation_priority_score, building_risk_score, building_assessment, renovation_roadmap, tco_10y_eur, tco_20y_eur, tco_30y_eur, cashflow_10y_eur, cashflow_20y_eur, cashflow_30y_eur, monthly_net_effect_eur, roi_equity_pct, roi_total_pct, seven_fold_returns, economics_scenarios, economics_block, subsidy_block, tax_potential_block, regulatory_risk_score, compliance_status, compliance_block, compliance_sources, customer_disclosure, compliance_engine_version, reasoning"
      )
      .eq("decision_run_id", data.run_id)
      .order("variant_code", { ascending: true });

    if (varErr) throw new Error(`Varianten konnten nicht geladen werden: ${varErr.message}`);

    const project = (runRow.projects as any) ?? {};

    const run: RunDetail = {
      id: runRow.id as string,
      engine_version: runRow.engine_version as string,
      ruleset_version: (runRow.ruleset_version as string | null) ?? null,
      prompt_version: (runRow.prompt_version as string | null) ?? null,
      phase_type: runRow.phase_type as string,
      status: runRow.status as string,
      started_at: (runRow.started_at as string | null) ?? null,
      finished_at: (runRow.finished_at as string | null) ?? null,
      trigger_source: (runRow.trigger_source as string | null) ?? null,
      economically_best_code: (runRow.economically_best_code as string | null) ?? null,
      energy_prime_code: (runRow.energy_prime_code as string | null) ?? null,
      tie_breaker_applied: (runRow.tie_breaker_applied as boolean | null) ?? null,
      tie_breaker_reason: (runRow.tie_breaker_reason as string | null) ?? null,
      output_summary: (runRow.output_summary as Record<string, any> | null) ?? null,
      last_error: (runRow.last_error as string | null) ?? null,
      created_at: runRow.created_at as string,
      project_no: project?.project_no ?? null,
      project_name: project?.name ?? null,
      project_id: runRow.project_id as string,
      variant_count: variants?.length ?? 0,
    };

    return { run, variants: (variants ?? []) as VariantSummary[] };
  });
